import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { CalendarCheck, Tag, Calculator, Check, MapPin, ShieldCheck } from "lucide-react";
import { CtaButton } from "@/components/site/CtaButton";
import { SectionHeading, Eyebrow } from "@/components/site/Sections";
import { Reveal } from "@/components/site/Reveal";
import { ServiceGrid } from "@/components/inspections/ServiceCard";
import { PriceCalculator } from "@/components/inspections/PriceCalculator";
import { StickyBookBar } from "@/components/inspections/StickyBookBar";
import { getInspectionServices, getInspectionOptions, money } from "@/lib/api";
import { INSPECTION_HERO_IMG, INSPECTION_EXPERIENCE_IMG, EXPERTISE, HIGHLIGHTS, BOOKING_STEPS, SERVICE_AREAS } from "@/data/inspections";

const Hero = ({ minPrice }) => (
  <section className="relative overflow-hidden border-b border-slate-200 bg-[#F5F3EC]">
    <div className="absolute inset-0 grid-texture opacity-40" />
    <div className="relative mx-auto grid max-w-7xl gap-12 px-4 py-16 lg:grid-cols-[1.15fr_1fr] lg:px-8 lg:py-24">
      <Reveal>
        <Eyebrow>Property Inspections · Pennsylvania</Eyebrow>
        <h1 data-testid="inspections-hero-title" className="mt-4 font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-[#0B132B] sm:text-5xl">
          9+ Years of Experience in Real Estate, Fix & Flip, Construction & Property Inspections
        </h1>
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg">
          Bringing over nine years of hands-on experience in real estate investments, fix-and-flip projects, construction management,
          renovation oversight, and property condition inspections.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <CtaButton to="/property-inspections/book" size="lg" data-testid="hero-book-inspection"><CalendarCheck className="h-4 w-4" /> Book Your Inspection</CtaButton>
          <CtaButton href="#pricing" variant="slate" size="lg" data-testid="hero-view-prices"><Tag className="h-4 w-4" /> View Inspection Prices</CtaButton>
        </div>
        <a href="#estimate" data-testid="hero-instant-estimate" className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#0B132B] underline decoration-gold decoration-2 underline-offset-4 hover:text-gold">
          <Calculator className="h-4 w-4 text-gold" /> Get an Instant Estimate
        </a>
      </Reveal>
      <Reveal delay={120} className="relative">
        <img src={INSPECTION_HERO_IMG} alt="Property inspector documenting a home" className="h-[380px] w-full rounded-3xl object-cover shadow-xl lg:h-full" />
        <div className="absolute -bottom-5 left-5 rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-lg">
          <p className="font-mono-x text-[10px] uppercase tracking-widest text-gold">Inspections from</p>
          <p data-testid="hero-min-price" className="font-display text-3xl font-bold text-[#0B132B]">{minPrice ? money(minPrice) : "…"}</p>
        </div>
      </Reveal>
    </div>
  </section>
);

const Highlights = () => (
  <section className="border-b border-slate-200 bg-white">
    <div className="mx-auto grid max-w-7xl gap-px bg-slate-200 sm:grid-cols-2 lg:grid-cols-4">
      {HIGHLIGHTS.map((h, i) => (
        <Reveal key={h.title} delay={i * 80} className="bg-white p-8">
          <ShieldCheck className="h-5 w-5 text-gold" />
          <h3 className="mt-4 font-display text-lg font-bold text-[#0B132B]">{h.title}</h3>
          <p className="mt-2 text-sm leading-relaxed text-slate-500">{h.text}</p>
        </Reveal>
      ))}
    </div>
  </section>
);

const Experience = () => (
  <section className="py-20 lg:py-28">
    <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 lg:grid-cols-2 lg:px-8">
      <img src={INSPECTION_EXPERIENCE_IMG} alt="Renovation project walkthrough" className="h-[420px] w-full rounded-3xl object-cover" />
      <div>
        <SectionHeading eyebrow="Professional Experience" title="Real estate, renovation and construction expertise in every inspection." />
        <ul className="mt-8 grid gap-3 sm:grid-cols-2">
          {EXPERTISE.map((e) => (
            <li key={e} className="flex gap-3 rounded-xl border border-slate-200 bg-[#FAF8F1] p-4 text-sm font-medium text-[#0B132B]">
              <Check className="h-4 w-4 shrink-0 text-gold" /> {e}
            </li>
          ))}
        </ul>
        <CtaButton to="/property-inspections/book" className="mt-8" data-testid="experience-schedule-btn">Schedule a Property Inspection</CtaButton>
      </div>
    </div>
  </section>
);

const Steps = () => (
  <section className="bg-[#0B132B] py-20 text-white lg:py-24">
    <div className="mx-auto max-w-7xl px-4 lg:px-8">
      <Eyebrow>Easy online scheduling</Eyebrow>
      <h2 className="mt-3 font-display text-2xl font-bold sm:text-3xl lg:text-4xl">Book in minutes. We confirm every request.</h2>
      <div className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
        {BOOKING_STEPS.map((s) => (
          <div key={s.n} className="border-t border-gold/40 pt-6">
            <span className="font-mono-x text-sm text-gold">{s.n}</span>
            <h3 className="mt-3 font-display text-xl font-bold">{s.title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-300">{s.text}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Areas = () => (
  <section id="service-area" className="py-20 lg:py-24">
    <div className="mx-auto max-w-7xl px-4 lg:px-8">
      <SectionHeading eyebrow="Service Area" title="Serving property owners and investors across Pennsylvania." />
      <div className="mt-10 flex flex-wrap gap-3">
        {SERVICE_AREAS.map((a) => (
          <span key={a} className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-5 py-2.5 text-sm text-[#0B132B]">
            <MapPin className="h-4 w-4 text-gold" /> {a}
          </span>
        ))}
      </div>
      <p className="mt-6 text-sm text-slate-500">Your ZIP code is checked during booking. Outside these areas? Call (215) 445-4257 to ask about availability.</p>
    </div>
  </section>
);

export default function PropertyInspections() {
  const [services, setServices] = useState([]);
  const [options, setOptions] = useState(null);
  const { hash } = useLocation();

  useEffect(() => {
    getInspectionServices().then(setServices).catch(() => setServices([]));
    getInspectionOptions().then(setOptions).catch(() => setOptions(null));
  }, []);

  useEffect(() => {
    if (hash && services.length) {
      document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
    }
  }, [hash, services.length]);

  const minPrice = services.length ? Math.min(...services.map((s) => s.base_price)) : null;

  return (
    <div data-testid="property-inspections-page">
      <Hero minPrice={minPrice} />
      <Highlights />
      <section id="pricing" className="bg-[#F5F3EC] py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <SectionHeading eyebrow="Transparent Pricing" title="Inspection services & starting prices" subtitle="Starting prices for properties up to 2,000 sq ft. Larger properties and extras are itemized in your estimate." />
            <CtaButton href="#estimate" variant="slate" data-testid="pricing-instant-estimate">Get an Instant Estimate</CtaButton>
          </div>
          <div className="mt-12">
            {services.length ? <ServiceGrid services={services} /> : <p className="text-sm text-slate-500">Loading services…</p>}
          </div>
        </div>
      </section>
      <section id="estimate" className="py-20 lg:py-28">
        <div className="mx-auto max-w-6xl px-4 lg:px-8">
          <SectionHeading eyebrow="Automatic Pricing" title="Get an instant estimate" subtitle="Select your inspection and enter property details. Every charge is shown before you book." />
          <div className="mt-10"><PriceCalculator services={services} options={options} /></div>
        </div>
      </section>
      <Experience />
      <Steps />
      <Areas />
      <section className="border-t border-slate-200 bg-[#F5F3EC] py-16">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-6 px-4 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-[#0B132B] sm:text-3xl">Ready to schedule your inspection?</h2>
          <CtaButton to="/property-inspections/book" size="lg" data-testid="footer-book-inspection">Book Your Inspection</CtaButton>
        </div>
      </section>
      <StickyBookBar />
    </div>
  );
}
